/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import { get, groupBy, sumBy } from 'lodash'

// Helper function to calculate the "All" summary
const calculateAllSummary = routesMetaDataByDay => {
  // Get all routes from all days
  const allRoutes = Object.values(routesMetaDataByDay)
    .filter(routes => Array.isArray(routes) && routes.length > 0)
    .flat()

  if (allRoutes.length === 0) return []

  // Group routes by ID
  const routesById = groupBy(allRoutes, 'id')

  // Create a summary for each unique route
  return Object.values(routesById).map(routeGroup => {
    const firstRoute = routeGroup[0]
    return {
      ...firstRoute,
      stopCount: sumBy(routeGroup, 'stopCount'),
    }
  })
}

// Each scheduling map + its sibling filters panel share one of these keys. They must be distinct:
// Map & routing keeps its map mounted behind the Schedule Stop dialog, so both write cap metadata
// at the same time against different addresses.
export const SCHEDULING_MAP_SURFACE = {
  MAP_ROUTING: 'mapRouting',
  NEW_SERVICE_SCHEDULE: 'newServiceSchedule',
  SCHEDULE_STOP: 'scheduleStop',
}

const EMPTY_RESULTS_META = { truncated: false, totalMatched: 0, cap: 0 }

const configuredServiceScheduleSlice = createSlice({
  name: 'configuredServiceSchedule',
  initialState: {
    routesMetaDataByDay: {},
    isLoadingRoutesByDay: false,
    resultsMetaBySurface: {},
  },
  reducers: {
    setRoutesMetaDataForDay: (state, action) => {
      const allSummary = calculateAllSummary(action.payload)
      state.routesMetaDataByDay = {
        ...action.payload,
        All: allSummary,
      }
    },
    clearRoutesMetaData: state => {
      state.routesMetaDataByDay = {}
      state.resultsMetaBySurface = {}
    },
    setRoutesLoading: (state, action) => {
      state.isLoadingRoutesByDay = Boolean(action.payload)
    },
    // The nearest-N cap lives with the map query but is surfaced in the filters panel, which
    // renders in a sibling column — this slice is how the two already share map metadata.
    setResultsMeta: (state, action) => {
      const { surface, truncated = false, totalMatched = 0, cap = 0 } = action.payload || {}
      if (!surface) return
      // Sessions persisted before this was keyed by surface rehydrate without the field.
      if (!state.resultsMetaBySurface) state.resultsMetaBySurface = {}
      state.resultsMetaBySurface[surface] = { truncated: Boolean(truncated), totalMatched, cap }
    },
    // Without this on unmount, closing a capped Schedule Stop dialog leaves its cap asserted over
    // the routing map still mounted behind it.
    clearResultsMeta: (state, action) => {
      const surface = action.payload
      if (!surface || !state.resultsMetaBySurface) return
      delete state.resultsMetaBySurface[surface]
    },
  },
})

export const { setRoutesMetaDataForDay, clearRoutesMetaData, setRoutesLoading, setResultsMeta, clearResultsMeta } =
  configuredServiceScheduleSlice.actions

export const selectRoutesMetaDataByDay = state => get(state, 'configuredServiceSchedule.routesMetaDataByDay', {})
export const selectAllRoutesMetaData = state => get(state, 'configuredServiceSchedule.routesMetaDataByDay.All', [])
export const selectRoutesByDayLoading = state => get(state, 'configuredServiceSchedule.isLoadingRoutesByDay', false)
export const selectSchedulingResultsMeta = (state, surface) =>
  get(state, ['configuredServiceSchedule', 'resultsMetaBySurface', surface], EMPTY_RESULTS_META)

export default configuredServiceScheduleSlice.reducer
